import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Avatar from '@justgiving/avatar';
import { deleteIcon, inlineMenuIcon, refreshIcon } from '@justgiving/icons';
import SVG from '@justgiving/svg';
import { captureException } from '@sentry/browser';

import { TimelineEntryType } from '../../../../types/__generated__';
import { useRefreshTimelineEntry } from '../../../hooks/timeline';
import { useApp } from '../../../hooks/useApp';
import { getBackendErrorMessage } from '../../../utils/getBackendErrorMessage';
import { makePhotoUrlFromAbsoluteUrl } from '../../../utils/makePhotoUrl';
import {
  DropdownMenu,
  DropdownMenuItem,
} from '../../DropdownMenu/DropdownMenu';
import { FormattedDate } from '../../Formatters/FormattedDate';
import IsPageOwner from '../../IsPageOwner/IsPageOwner';
import { TimelineEntryProps } from './TimelineEntry';

import timelineStyles from '../Timeline.scss';
import timelineEntryStyles from '../TimelineEntry/TimelineEntry.scss';

type HeaderProps = Pick<
  TimelineEntryProps,
  'timelineEntry' | 'avatarUrl' | 'postedBy'
> & {
  avatarBadge?: ReactNode;
  disableRefreshButton?: boolean;
  onDeleteEntry?: (id: string) => void;
};

export const Header: FunctionComponent<HeaderProps> = ({
  timelineEntry,
  avatarUrl,
  postedBy = '',
  avatarBadge,
  onDeleteEntry = () => {},
  disableRefreshButton,
  children,
}) => {
  const { id, createdAt } = timelineEntry;
  const { t } = useTranslation('common', { useSuspense: false });
  const [refreshTimelineEntry, { error }] = useRefreshTimelineEntry(id);
  const { showToast } = useApp();
  const [disableRefresh, setDisableRefresh] = useState(disableRefreshButton);

  useEffect(() => {
    if (error) {
      showToast({
        type: 'warning',
        message: getBackendErrorMessage(error),
        dismissAfter: 10000,
      });
    }
  }, [error, showToast]);

  const handleRefreshTimelineEntry = async () => {
    try {
      await refreshTimelineEntry();
      showToast({
        type: 'success',
        message: t('timeline.refreshTimelineEntryRequested'),
        dismissAfter: 5000,
      });

      setDisableRefresh(true);
    } catch (error) {
      captureException(error);
    }
  };

  const buildMenuItems = () => {
    const items: DropdownMenuItem[] = [
      {
        key: 'delete',
        icon: deleteIcon,
        label: timelineEntry.fitnessActivity
          ? t('timeline.deleteFitnessTimelineEntry.label')
          : t('timeline.deleteNonFitnessTimelineEntry.label'),
        onClick: () => onDeleteEntry(id),
      },
    ];

    if (
      timelineEntry.type === TimelineEntryType.Fitness &&
      timelineEntry.publishingSource
    ) {
      items.push({
        key: 'refresh',
        icon: refreshIcon,
        label: disableRefresh
          ? t('timeline.refreshingTimelineEntry.label')
          : t('timeline.refreshTimelineEntry.label'),
        onClick: handleRefreshTimelineEntry,
        disabled: disableRefresh,
      });
    }

    return items;
  };

  return (
    <div className={timelineStyles.header}>
      <div aria-label={t('timeline.avatar')} className={timelineStyles.avatar}>
        <Avatar
          avatarSize="large"
          picture={
            avatarUrl ? makePhotoUrlFromAbsoluteUrl(avatarUrl) : undefined
          }
        />
        {avatarBadge ? (
          <span className={timelineEntryStyles.avatarBadge}>{avatarBadge}</span>
        ) : null}
      </div>
      <div className="jg-display-flex jg-flex-g1 jg-flex-aic">
        <div className="jg-flex-g1">
          <h2 className={timelineStyles.title}>{postedBy}</h2>
          <p className={timelineEntryStyles.subtitle}>
            <FormattedDate value={createdAt} />
          </p>
        </div>
        <IsPageOwner>
          <DropdownMenu
            ariaLabel={t('timeline.menu')}
            items={buildMenuItems()}
            icon={<SVG markup={inlineMenuIcon} />}
          />
        </IsPageOwner>
      </div>
      {children}
    </div>
  );
};
Header.displayName = 'TimelineEntryHeader';
